/** @jsx jsx */
import { Box, Container } from "@eworkit/iris";
import { FC, useCallback, useContext, useMemo } from "react";
import { jsx } from "theme-ui";

import { AppSuspense } from "@/components/app";
import { UserContext } from "@/components/User";
import { TimeReport } from "@/data/time-reports";
import { RoutedTabPanels } from "@/router/RoutedTabPanels";

import { TimeReportDetailsCalendarTab } from "./TimeReportDetailsCalendarTab";
import { TimeReportDetailsLifecycleTab } from "./TimeReportDetailsLifecycleTab";
import { TimeReportDetailsRelatedTab } from "./TimeReportDetailsRelatedTab";
import { TimeReportDetailsSpecificationTab } from "./TimeReportDetailsSpecificationTab";
import { TimeReportDetailsSummaryTab } from "./TimeReportDetailsSummaryTab";
import { TimeReportDetailsVersionsTab } from "./TimeReportDetailsVersionsTab";

interface TimeReportDetailsTabPanelsProps {
  timeReport: TimeReport;
}

export const TimeReportDetailsTabPanels: FC<TimeReportDetailsTabPanelsProps> = ({ timeReport }) => {
  const { hasCompanyManagerPrivileges } = useContext(UserContext);

  const calendar = useCallback(() => <TimeReportDetailsCalendarTab timeReport={timeReport} />, [timeReport]);
  const specification = useCallback(() => <TimeReportDetailsSpecificationTab timeReport={timeReport} />, [timeReport]);
  const summary = useCallback(() => <TimeReportDetailsSummaryTab timeReport={timeReport} />, [timeReport]);
  const lifecycle = useCallback(() => <TimeReportDetailsLifecycleTab timeReport={timeReport} />, [timeReport]);
  const versions = useCallback(() => <TimeReportDetailsVersionsTab timeReportGuid={timeReport.guid} />, [timeReport]);
  const related = useCallback(() => <TimeReportDetailsRelatedTab timeReport={timeReport} />, [timeReport]);

  const tabPanels = useMemo(
    () =>
      hasCompanyManagerPrivileges()
        ? {
            summary,
            calendar,
            specification,
            lifecycle,
            versions,
            related,
          }
        : {
            calendar,
            specification,
            summary,
            lifecycle,
            versions,
          },
    [hasCompanyManagerPrivileges, summary, calendar, specification, lifecycle, versions, related],
  );

  return (
    <Container>
      <Box px={[null, null, null, "sm"]} py={["xs", null, null, "md"]}>
        <AppSuspense>
          <RoutedTabPanels routeParam="tab" views={tabPanels} />
        </AppSuspense>
      </Box>
    </Container>
  );
};
